import { FC } from 'react'
import { EmployeeAddressFragment } from '../../../types'
import PerText from '../../../components/custom_components/Text'
import { Table } from 'antd'
import { useTranslation } from 'react-i18next'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { SortOrder } from 'antd/lib/table'
import { PerTag } from '../../../components/custom_components/Tag'

interface AddressesTableProps {
  addresses: EmployeeAddressFragment[]
}

export const AdressesTable: FC<AddressesTableProps> = ({ addresses }) => {
  const { t } = useTranslation()
  const dataSource = addresses.map((address) => ({ ...address, key: address.addressId }))

  const tagColor = (type: string) => {
    if (type === 'PRIMARY') {
      return 'cyan'
    }
    if (type === 'SECONDARY') {
      return undefined
    }
    return undefined // fallback
  }

  const addressColumns = [
    {
      key: 'address-address',
      title: t('profile.address.address'),
      width: '30%',
      render: (address: EmployeeAddressFragment) => {
        const addressLine1 = address.address && address.address.addressLine1 ? address.address.addressLine1 : undefined
        const addressLine2 = address.address && address.address.addressLine2 ? address.address.addressLine2 : undefined
        const addressLine3 = address.address && address.address.addressLine3 ? address.address.addressLine3 : undefined
        const zipCode = address.address && address.address.zipPostalCode ? address.address.zipPostalCode : undefined
        return (
          <>
            {addressLine1 && <PerText>{addressLine1}</PerText>}
            {(addressLine2 || addressLine3 || zipCode) && (
              <PerText>
                {zipCode && zipCode}
                {addressLine2 && ` ${addressLine2}`}
                {(addressLine2 || zipCode) && addressLine3 && ` ${addressLine3}`}
              </PerText>
            )}
          </>
        )
      },
    },
    {
      key: 'address-stateRegion',
      title: t('profile.address.stateRegion'),
      width: '30%',
      render: (address: EmployeeAddressFragment) =>
        address.address && address.address.stateRegion && <PerText>{address.address.stateRegion}</PerText>,
    },
    {
      key: 'address-phoneNumber',
      title: t('profile.address.phoneNumber'),
      width: '30%',
      render: (address: EmployeeAddressFragment) =>
        address.address && address.address.phoneNumber && <PerText>{address.address.phoneNumber}</PerText>,
    },
    {
      key: 'address-type',
      title: 'Type',
      width: '10%',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (typeA: EmployeeAddressFragment, typeB: EmployeeAddressFragment) => {
        const a = typeA.type
        const b = typeB.type
        return a.localeCompare(b)
      },
      render: (address: EmployeeAddressFragment) => (
        <PerTag color={tagColor(address.type)}>{t(`employee.address.type.${address.type}`)}</PerTag>
      ),
    },
  ]

  const isColumnNotEmpty = (columnKey: string): boolean => {
    return dataSource.some((addressObj) => {
      if (!addressObj.address) {
        return false
      }

      switch (columnKey) {
        case 'address-address':
          return (
            addressObj.address.addressLine1 ||
            addressObj.address.addressLine2 ||
            addressObj.address.addressLine3 ||
            addressObj.address.zipPostalCode
          )
        case 'address-stateRegion':
          return addressObj.address.stateRegion
        case 'address-phoneNumber':
          return addressObj.address.phoneNumber
        case 'address-type':
          return addressObj.type
        default:
          return true
      }
    })
  }

  const filteredColumns = addressColumns.filter((col) => isColumnNotEmpty(col.key))

  return (
    <>
      <PerText appearance="tile-heading" mb="s">
        {t('profile.address.header')}
      </PerText>
      <Table
        className="small"
        rowClassName={() => 'minHeight-row'}
        dataSource={dataSource}
        columns={filteredColumns}
        pagination={false}
        locale={{
          emptyText: <PerEmpty info={t('profile.address.emptyTable')} />,
        }}
      />
    </>
  )
}
